import React, { useState, useRef } from 'react'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import './CreateImprovementPlan.css'
import { FaSave, FaSpinner, FaUpload, FaTrash, FaImage } from 'react-icons/fa'
import { toast } from 'react-toastify'
import axios from 'axios'
import Cookies from 'js-cookie'
import API_BASE_URL from '../../../config/api'

// Signature Upload Component
const SignatureUpload = ({ onFileSelect, onClear, signatureFile, setSignatureFile }) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setSignatureFile(file);
    onFileSelect(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    toast.success('Signature image uploaded successfully');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

};

const CreateImprovementPlan = () => {
  const [formData, setFormData] = useState({
    strengths: '',
    weaknesses: '',
    employee_comment: '',
    training_needs: '',
    employee_signature: null
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const baseUrl = API_BASE_URL

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.strengths.trim()) {
      newErrors.strengths = 'Strengths are required';
    }
    
    if (!formData.weaknesses.trim()) {
      newErrors.weaknesses = 'Weaknesses are required';
    }
    
    if (!formData.employee_comment.trim()) {
      newErrors.employee_comment = 'Employee comment is required';
    }
    
    if (!formData.training_needs.trim()) {
      newErrors.training_needs = 'Training needs are required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = Cookies.get('token');
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('strengths', formData.strengths);
      submitData.append('weaknesses', formData.weaknesses);
      submitData.append('employee_comment', formData.employee_comment);
      submitData.append('training_needs', formData.training_needs);
      
      const response = await axios.post(
        `${baseUrl}/api/improvement-plans/`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Improvement plan created successfully!');
      
      // Reset form
      setFormData({
        strengths: '',
        weaknesses: '',
        employee_comment: '',
        training_needs: '',
        employee_signature: null
      });
      
    } catch (error) {
      console.error('Error creating improvement plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to create improvement plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div  className="employee-goals-container">
        <div className="page-header">
          <div className="header-content">
            <div className="page-title">
              <h3>Create Improvement Plan</h3>
              <p>Add a new improvement plan for next year</p>
            </div>
          </div>
        </div>
        
        <div style={{padding: '20px'}} className="formcontainer">
          <form onSubmit={handleSubmit} className="improvement-plan-form">
            <div className="form-section">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="strengths">Strengths *</label>
                  <textarea
                    id="strengths"
                    name="strengths"
                    value={formData.strengths}
                    onChange={handleInputChange}
                    placeholder="Describe your key strengths and areas where you excel..."
                    className={errors.strengths ? 'error' : ''}
                    rows={4}
                    cols={1}
                  />
                  {errors.strengths && <span className="error-message">{errors.strengths}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="weaknesses">Weaknesses *</label>
                  <textarea
                    id="weaknesses"
                    name="weaknesses"
                    value={formData.weaknesses}
                    onChange={handleInputChange}
                    placeholder="Identify areas for improvement and development..."
                    className={errors.weaknesses ? 'error' : ''}
                    rows={4}
                    cols={1}
                  />
                  {errors.weaknesses && <span className="error-message">{errors.weaknesses}</span>}
                </div>
              </div>
            
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="employee_comment">Comment *</label>
                  <textarea
                    id="employee_comment"
                    name="employee_comment"
                    value={formData.employee_comment}
                    onChange={handleInputChange}
                    placeholder="Share your thoughts, concerns, or suggestions about your performance and development..."
                    className={errors.employee_comment ? 'error' : ''}
                    rows={4}
                    cols={1}
                  />
                  {errors.employee_comment && <span className="error-message">{errors.employee_comment}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="training_needs">Training Needs *</label>
                  <textarea
                    id="training_needs"
                    name="training_needs"
                    value={formData.training_needs}
                    onChange={handleInputChange}
                    placeholder="Specify the training, courses, or development activities you need..."
                    className={errors.training_needs ? 'error' : ''}
                    rows={4}
                    cols={1}
                  />
                  {errors.training_needs && <span className="error-message">{errors.training_needs}</span>}
                </div>
              </div>
            </div>
            
            
          
            
            <div className="form-actions">
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Create Improvement Plan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default CreateImprovementPlan